<template>
    <div class="container">
        <competition-year-header :year="year"/>
        <base-alert v-if="year == 2022" class="mb-4">
            <template #header>Pravidla</template>
            <ul>
                <li>Tipněte si konečné <strong>umístění závodníků na prvních 25 pozicích</strong>.</li>
                <li>Za každý správný tip je jeden bod.</li>
                <li>Kdo získá nejvíce bodů, vyhraje tipovací soutěž.</li>
                <li>V případě shodného počtu bodů rozhoduje datum a čas zaslání tipů (kdo dřív pošle, vyhrává)</li>
                <li>Nelze tipnout stejné umístění dvěma a více závodníkům.</li>
                <li>Nutno tipnout všech prvních 25 umístění, jinak je vaše tipování neplatné.</li>
                <li>Tipovat mohou pouze účastníci letošního ročníku.</li>
                <li>Vaše tipy budou zveřejněny na těchto stránkách</li>
                <li>Pro <strong>tři nejlepší</strong> tipaře bude přichystána <strong>hodnotná výhra</strong></li>
                <li>Vyhodnocení proběhne po závodě na after-party</li>
            </ul>
            <div class="mt-2"><strong>Zasílání tipů uzavřeno 31.7.2022 23:59.</strong>
            </div>
        </base-alert>
        <div class="table-responsive">
            <table class="table table-bordered w-auto">
                <thead>
                <tr>
                    <th colspan="4"></th>
                    <th :colspan="competitors.length">Účastníci tipovací soutěže ({{ competitors.length }})</th>
                </tr>
                <tr>
                    <th>Závodníci</th>
                    <th>Umístění</th>
                    <th>Průměrný tip</th>
                    <th>Medián</th>
                    <th v-for="({name, date}, index) in competitors" :key="index">
                        <link-person :person="name"/>
                        <br/>
                        <span class="date">{{ date }}</span>
                    </th>
                </tr>
                <tr>
                    <th>Správné tipy</th>
                    <th v-for="i in 3" :key="'empty-' + i"></th>
                    <th v-for="(sum, index) in competitorsTipsSums" :key="index">
                        {{ sum }}
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(racer, racerIndex) in racers" :key="racerIndex">
                    <td>
                        <link-person :person="racer"/>
                    </td>
                    <td :title="`Reálné umístění ${year}`">
                        <base-medal v-if="results && results[racerIndex]" :order="results[racerIndex]"/>
                    </td>
                    <td title="Zprůměrovaný tip (zaokrouhleno)">
                        <base-medal v-if="averages[racerIndex]" :order="averages[racerIndex]"/>
                    </td>
                    <td title="Medián (zaokrouhleno)">
                        <base-medal v-if="medians[racerIndex]" :order="medians[racerIndex]"/>
                    </td>
                    <td v-for="(competitor, competitorIndex) in competitors"
                        :key="competitorIndex"
                        :title="`Tipuje: ${competitor.name}`"
                        class="text-right"
                        :class="{correct: results[racerIndex] && competitor.tips[racerIndex] === results[racerIndex]}"
                    >
                        {{ competitor.tips[racerIndex] }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import TimeComparisonCell from "@/components/TimeComparisonCell";
import BaseMedal from "@/components/BaseMedal";
import HeaderCell from "@/components/HeaderCell";
import TableHeader from "@/components/TableHeader";
import Registration from "@/components/Registration";
import LinkPerson from "@/components/LinkPerson";
import BaseAlert from "@/components/BaseAlert";
import CompetitionYearHeader from "@/components/CompetitionYearHeader";

export default {
    components: {
        CompetitionYearHeader,
        BaseAlert, LinkPerson, Registration, TableHeader, HeaderCell, BaseMedal, TimeComparisonCell},
    computed: {
        year() {
            return parseInt(this.$route.params.year);
        },
        competition() {
            return this.$store.state.data[this.year].competition;
        },
        competitors() {
            return this.competition.competitors;
        },
        competitorsTipsSums() {
            return [...Array(this.competitors.length).keys()]
            .map(competitorIndex => [...Array(this.racers.length).keys()]
                .reduce((sum, racerIndex) =>
                        sum + (
                            (this.results[racerIndex] && this.competitors[competitorIndex].tips[racerIndex] === this.results[racerIndex])
                                ? 1 : 0.
                        ),
                    0
                )
            );
        },
        racers() {
            return this.competition.racers;
        },
        validTipsPerRacer() {
            return this.racers
            .map((racer, racerIndex) =>
                this.competitors.reduce((tips, competitor) => {
                    const tip = competitor.tips[racerIndex];
                    return tip ? [...tips, tip] : tips;
                }, [])
            );
        },
        averages() {
            return this.validTipsPerRacer.map(tips =>
                tips.length > 0
                    ? Math.round(tips.reduce((sum, tip) => sum + tip, 0) / tips.length)
                    : null
            );
        },
        medians() {
            const asc = arr => arr.sort((a, b) => a - b);
            const quantile = (arr, q) => {
                const sorted = asc(arr);
                const pos = (sorted.length - 1) * q;
                const base = Math.floor(pos);
                const rest = pos - base;
                if (sorted[base + 1] !== undefined) {
                    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
                } else {
                    return sorted[base];
                }
            };
            const q50 = arr => quantile(arr, .50);
            return this.validTipsPerRacer
            .map(tips => q50(tips))
            .map(median => Math.round(median));
        },
        resultsByYear() {
            return this.$store.getters.resultsByYear(this.year);
        },
        results() {
            return this.racers
            .map(racer => this.resultsByYear && this.resultsByYear.find(result => result.person === racer))
            .map(racerResult => racerResult ? racerResult.order : null);
        }
    },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables";

tr {
    td, th {
        &:nth-child(4), &:last-child, &:first-child {
            border-right-color: #333;
        }

        &:first-child {
            border-left-color: #333;
        }
    }

    &:first-child {
        th {
            border-top-color: #333;
        }
    }

    &:last-child {
        th, td {
            border-bottom-color: #333;
        }
    }
}

thead {
    tr:nth-child(2) th:nth-child(n+2) {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        padding: 0.75rem 0.25rem;
    }

    .date {
        font-weight: normal;
        font-size: 0.9em;
    }
}

tbody {
    tr {
        &:hover {
            background-color: #e3d4ff;
        }

        td:nth-child(n+2) {
            cursor: help;
        }
    }
}

.correct {
    background-color: $green;
    color: white;
    font-weight: bold;
}

.table td {
    cursor: inherit;
}
</style>